/**
 * Central configuration for all available Prebid.js bidders and modules
 * This serves as the single source of truth for dashboard options
 */

export const AVAILABLE_BIDDERS = {
  adf: {
    label: 'ADF (Adform)',
    description: 'ADF bid adapter',
    requiredParams: ['mid'],
    optionalParams: ['mname', 'priceType'],
    globalParams: [], // No global parameters
    placementParams: ['mid', 'mname', 'priceType'], // All placement-specific
    defaultConfig: {
      enabled: false,
      mid: null
    },
    paramTypes: {
      mid: 'string',
      mname: 'string',
      priceType: 'string'
    },
    paramDescriptions: {
      mid: 'Placement ID (Master ID)',
      mname: 'Master Name',
      priceType: 'Price Type'
    },
    tcf2: {
      requiresStorage: true,
      purposes: ['basicAds', 'personalizedAds', 'measurement'],
      needsVendorException: false,
      gvlId: 50
    },
    storage: {
      deviceAccess: true,
      cookieAccess: true,
      localStorageAccess: true
    }
  },
  appnexus: {
    label: 'AppNexus',
    description: 'AppNexus bid adapter',
    requiredParams: ['placementId'],
    optionalParams: ['keywords', 'targetingParams', 'allowSmallerSizes', 'usePaymentRule'],
    globalParams: [], // No global parameters
    placementParams: ['placementId', 'keywords', 'targetingParams', 'allowSmallerSizes', 'usePaymentRule'], // All placement-specific
    defaultConfig: {
      enabled: false,
      placementId: null
    },
    paramTypes: {
      placementId: 'string',
      keywords: 'object',
      targetingParams: 'object',
      allowSmallerSizes: 'boolean',
      usePaymentRule: 'boolean'
    },
    paramDescriptions: {
      placementId: 'Placement ID',
      keywords: 'Keywords for targeting',
      targetingParams: 'Additional targeting parameters',
      allowSmallerSizes: 'Allow smaller ad sizes',
      usePaymentRule: 'Use payment rule'
    },
    tcf2: {
      requiresStorage: true,
      purposes: ['basicAds', 'personalizedAds', 'measurement'],
      needsVendorException: false,
      gvlId: 32
    },
    storage: {
      deviceAccess: true,
      cookieAccess: true,
      localStorageAccess: true
    }
  },
  criteo: {
    label: 'Criteo',
    description: 'Criteo bid adapter',
    requiredParams: ['networkId'],
    optionalParams: [],
    globalParams: ['networkId'], // Global (site-level) parameters
    placementParams: [], // Placement-specific parameters  
    defaultConfig: {
      enabled: false,
      networkId: null
    },
    paramTypes: {
      networkId: 'number'
    },
    paramDescriptions: {
      networkId: 'Network ID'
    },
    preferredUserIds: ['criteoId'],
    supportedUserIds: ['criteoId', 'id5Id'],
    tcf2: {
      requiresStorage: true,
      purposes: ['basicAds', 'personalizedAds', 'measurement'],
      needsVendorException: false,
      gvlId: 91
    },
    storage: {
      deviceAccess: true,
      cookieAccess: true,
      localStorageAccess: true
    }
  },
  smartadserver: {
    label: 'Smart AdServer',
    description: 'Smart AdServer bid adapter',
    requiredParams: ['siteId', 'formatId'],
    optionalParams: ['pageId'],
    globalParams: ['siteId'], // Global (site-level) parameters
    placementParams: ['formatId', 'pageId'], // Placement-specific parameters
    defaultConfig: {
      enabled: false,
      siteId: null,
      formatId: null,
      pageId: null
    },
    paramTypes: {
      siteId: 'number',
      formatId: 'number',
      pageId: 'number'
    },
    paramDescriptions: {
      siteId: 'Site ID',
      formatId: 'Format ID',
      pageId: 'Page ID'
    },
    tcf2: {
      requiresStorage: true,
      purposes: ['basicAds', 'personalizedAds', 'measurement'],
      needsVendorException: false,
      gvlId: 45
    },
    storage: {
      deviceAccess: true,
      cookieAccess: true,
      localStorageAccess: true
    }
  },
  projectagora: {
    label: 'Project Agora (AppNexus Alias)',
    description: 'Project Agora bid adapter - AppNexus alias with automatic 0.75 bid adjustment',
    requiredParams: ['placementId'],
    optionalParams: ['sid', 'keywords', 'targetingParams', 'allowSmallerSizes', 'usePaymentRule'],
    globalParams: ['sid'], // Global (site-level) parameters
    placementParams: ['placementId', 'keywords', 'targetingParams', 'allowSmallerSizes', 'usePaymentRule'], // Placement-specific
    defaultConfig: {
      enabled: false,
      placementId: null,
      sid: null
    },
    paramTypes: {
      placementId: 'string',
      sid: 'string',
      keywords: 'object',
      targetingParams: 'object',
      allowSmallerSizes: 'boolean',
      usePaymentRule: 'boolean'
    },
    paramDescriptions: {
      placementId: 'Placement ID (AppNexus format)',
      sid: 'Seller ID for schain (e.g., 109575 for b365)',
      keywords: 'Keywords for targeting',
      targetingParams: 'Additional targeting parameters',
      allowSmallerSizes: 'Allow smaller ad sizes',
      usePaymentRule: 'Use payment rule'
    },
    isAlias: true,
    aliasOf: 'appnexus',
    specialNote: 'Automatically applies 0.75 bid adjustment (gross to net) and schain configuration'
  },
  sovrn: {
    label: 'Sovrn',
    description: 'Sovrn bid adapter',
    requiredParams: ['tagid'],
    optionalParams: ['adunitcode'],
    globalParams: [], // No global parameters
    placementParams: ['tagid', 'adunitcode'], // All parameters are placement-specific
    defaultConfig: {
      enabled: false,
      tagid: null
    },
    paramTypes: {
      tagid: 'string',
      adunitcode: 'string'
    },
    paramDescriptions: {
      tagid: 'Sovrn Ad Tag ID',
      adunitcode: 'Ad unit identifier'
    },
    tcf2: {
      requiresStorage: true,
      purposes: ['basicAds', 'measurement'],
      needsVendorException: true,
      gvlId: 13
    },
    storage: {
      deviceAccess: true,
      cookieAccess: true,
      localStorageAccess: false
    },
    preferredUserIds: ['id5Id'],
    supportedUserIds: ['id5Id', 'criteoId']
  },
  teads: {
    label: 'Teads',
    description: 'Teads bid adapter',
    requiredParams: ['placementId', 'pageId'],
    optionalParams: [],
    globalParams: [], // No global parameters
    placementParams: ['placementId', 'pageId'], // All parameters are placement-specific
    defaultConfig: {
      enabled: false,
      placementId: null,
      pageId: null
    },
    paramTypes: {
      placementId: 'number',
      pageId: 'number'
    },
    paramDescriptions: {
      placementId: 'Teads Placement ID',
      pageId: 'Teads Page ID'
    },
    preferredUserIds: ['teadsId'],
    supportedUserIds: ['teadsId', 'id5Id', 'criteoId'],
    tcf2: {
      requiresStorage: true,
      purposes: ['basicAds', 'personalizedAds'],
      needsVendorException: false,
      gvlId: 132
    },
    storage: {
      deviceAccess: true,
      cookieAccess: true,
      localStorageAccess: true
    }
  },
  triplelift: {
    label: 'TripleLift',
    description: 'TripleLift bid adapter',
    requiredParams: ['inventoryCode'],
    optionalParams: [],
    globalParams: [], // No global parameters
    placementParams: ['inventoryCode'], // Placement-specific parameters
    defaultConfig: {
      enabled: false,
      inventoryCode: null
    },
    paramTypes: {
      inventoryCode: 'string'
    },
    paramDescriptions: {
      inventoryCode: 'TripleLift inventory code for the ad unit'
    },
    tcf2: {
      requiresStorage: true,
      purposes: ['basicAds', 'personalizedAds', 'measurement'],
      needsVendorException: false,
      gvlId: 28
    },
    storage: {
      deviceAccess: true,
      cookieAccess: true,
      localStorageAccess: true
    }
  },
  sspBC: {
    label: 'sspBC',
    description: 'sspBC bid adapter with automatic inventory mapping',
    requiredParams: [],
    optionalParams: [],
    defaultConfig: {
      enabled: false
    },
    paramTypes: {},
    paramDescriptions: {},
    tcf2: {
      requiresStorage: true,
      purposes: ['basicAds', 'personalizedAds', 'measurement'],
      needsVendorException: false,
      gvlId: 676
    },
    storage: {
      deviceAccess: true,
      cookieAccess: true,
      localStorageAccess: true
    },
    specialNote: 'No bid parameters required - sspBC handles inventory mapping automatically (1-2 day setup period)'
  }
};

export const AVAILABLE_MODULES = {
  // Core Modules
  priceFloors: {
    label: 'Price Floors',
    description: 'Dynamic floor pricing',
    category: 'Core',
    defaultEnabled: true
  },
  consentManagementTcf: {
    label: 'Consent Management TCF',
    description: 'GDPR TCF v2.0 compliance',
    category: 'Core',
    defaultEnabled: true
  },
  currency: {
    label: 'Currency',
    description: 'Multi-currency support',
    category: 'Core',
    defaultEnabled: true
  },
  userId: {
    label: 'User ID',
    description: 'User identification modules',
    category: 'Core',
    defaultEnabled: true
  },
  gptPreAuction: {
    label: 'GPT Pre-Auction',
    description: 'Enhanced Google Ad Manager integration',
    category: 'Core',
    defaultEnabled: true
  },
  schain: {
    label: 'Supply Chain',
    description: 'Supply chain transparency',
    category: 'Core',
    defaultEnabled: true
  },
  tcfControl: {
    label: 'TCF Control',
    description: 'TCF privacy control module',
    category: 'Core',
    defaultEnabled: true
  }
};

export const AVAILABLE_USER_ID_MODULES = {
  criteoId: {
    label: 'Criteo ID',
    description: 'Criteo user identification',
    category: 'User ID',
    defaultEnabled: true,
    defaultConfig: {
      enabled: true,
      expiry: 30,
      bidders: ['criteo'],
      storage: {
        type: 'html5',
        name: 'criteo',
        refreshInSeconds: null
      }
    },
    preferredBidders: ['criteo'],
    supportedBidders: ['criteo'],
    paramTypes: {
      expiry: 'number',
      bidders: 'array',
      storage: 'object'
    },
    paramDescriptions: {
      expiry: 'Cookie expiry in days',
      bidders: 'Bidders that receive this ID',
      storage: 'Storage configuration options'
    },
    storageTypes: ['html5', 'cookie', 'both']
  },
  teadsId: {
    label: 'Teads ID',
    description: 'Teads user identification',
    category: 'User ID',
    defaultEnabled: true,
    defaultConfig: {
      enabled: true,
      pubId: 26501,
      expiry: 30,
      bidders: ['teads'],
      storage: {
        type: 'html5',
        name: 'teads-userid',
        refreshInSeconds: null
      }
    },
    preferredBidders: ['teads'],
    supportedBidders: ['teads', 'smartadserver'],
    paramTypes: {
      pubId: 'number',
      expiry: 'number',
      bidders: 'array',
      storage: 'object'
    },
    paramDescriptions: {
      pubId: 'Teads Publisher ID',
      expiry: 'Cookie expiry in days',
      bidders: 'Bidders that receive this ID',
      storage: 'Storage configuration options'
    },
    storageTypes: ['html5', 'cookie', 'both']
  },
  id5Id: {
    label: 'ID5 Universal ID',
    description: 'ID5 universal user identifier',
    category: 'User ID',
    defaultEnabled: false, // Not enabled by default for new sites
    defaultConfig: {
      enabled: false, // Must be explicitly enabled by user
      partner: null,
      expiry: 30,
      bidders: [],
      storage: {
        type: 'html5',
        name: 'id5id',
        refreshInSeconds: null
      }
    },
    preferredBidders: [],
    supportedBidders: ['criteo', 'teads', 'smartadserver', 'triplelift', 'sovrn', 'adf'],
    paramTypes: {
      partner: 'number',
      expiry: 'number',
      bidders: 'array',
      storage: 'object'
    },
    paramDescriptions: {
      partner: 'ID5 Partner ID (required)',
      expiry: 'Cookie expiry in days',
      bidders: 'Bidders that receive this ID',
      storage: 'Storage configuration options'
    },
    storageTypes: ['html5', 'cookie', 'both']
  }
};

/**
 * Get default configuration for a new site
 */
export function getDefaultSiteConfig() {
  const defaultBidders = {};
  const defaultModules = [];
  const defaultUserIdModules = {};

  // Add all bidders with default configurations
  Object.keys(AVAILABLE_BIDDERS).forEach(bidderName => {
    defaultBidders[bidderName] = { ...AVAILABLE_BIDDERS[bidderName].defaultConfig };
  });

  // Add default enabled modules
  Object.keys(AVAILABLE_MODULES).forEach(moduleName => {
    if (AVAILABLE_MODULES[moduleName].defaultEnabled) {
      defaultModules.push(moduleName);
    }
  });

  // Add default enabled user ID modules
  Object.keys(AVAILABLE_USER_ID_MODULES).forEach(moduleName => {
    if (AVAILABLE_USER_ID_MODULES[moduleName].defaultEnabled) {
      defaultUserIdModules[moduleName] = { ...AVAILABLE_USER_ID_MODULES[moduleName].defaultConfig };
    }
  });

  return {
    bidders: defaultBidders,
    modules: defaultModules,
    userIdModules: defaultUserIdModules
  };
}

/**
 * Validate bidder configuration
 */
export function validateBidderConfig(bidderName, config) {
  const bidderDef = AVAILABLE_BIDDERS[bidderName];
  if (!bidderDef) {
    return { valid: false, errors: [`Unknown bidder: ${bidderName}`] };
  }

  const errors = [];
  
  // Check required parameters
  bidderDef.requiredParams.forEach(param => {
    if (config.enabled && (config[param] === null || config[param] === undefined)) {
      errors.push(`Missing required parameter: ${param}`);
    }
  });

  // Validate parameter types
  Object.keys(config).forEach(param => {
    if (param === 'enabled') return;
    
    const expectedType = bidderDef.paramTypes[param];
    if (expectedType && config[param] !== null && config[param] !== undefined) {
      const actualType = typeof config[param];
      if (actualType !== expectedType) {
        errors.push(`Parameter ${param} should be ${expectedType}, got ${actualType}`);
      }
    }
  });

  return { valid: errors.length === 0, errors };
}

/**
 * Get combined available modules for UI display
 */
export function getAvailableModulesForUI() {
  const allModules = [];
  
  // Add core modules
  Object.keys(AVAILABLE_MODULES).forEach(moduleId => {
    allModules.push({
      id: moduleId,
      ...AVAILABLE_MODULES[moduleId]
    });
  });

  // Add user ID modules
  Object.keys(AVAILABLE_USER_ID_MODULES).forEach(moduleId => {
    allModules.push({
      id: moduleId,
      ...AVAILABLE_USER_ID_MODULES[moduleId]
    });
  });

  return allModules;
}

/**
 * Get optimal User ID configuration based on enabled bidders
 */
export function getOptimalUserIdConfig(enabledBidders) {
  const userIdCounts = {};
  const recommendations = [];
  
  // Count how many bidders prefer each User ID
  enabledBidders.forEach(bidderName => {
    const bidderDef = AVAILABLE_BIDDERS[bidderName];
    if (bidderDef && bidderDef.preferredUserIds) {
      bidderDef.preferredUserIds.forEach(userIdType => {
        userIdCounts[userIdType] = (userIdCounts[userIdType] || 0) + 1;
      });
    }
  });
  
  // Sort by usage count and recommend top User IDs
  Object.entries(userIdCounts)
    .sort(([,a], [,b]) => b - a)
    .forEach(([userIdType, count]) => {
      const userIdModule = AVAILABLE_USER_ID_MODULES[userIdType];
      if (userIdModule) {
        recommendations.push({
          id: userIdType,
          label: userIdModule.label,
          bidderCount: count,
          bidders: enabledBidders.filter(bidder => 
            AVAILABLE_BIDDERS[bidder]?.preferredUserIds?.includes(userIdType)
          )
        });
      }
    });
    
  return recommendations;
}

/**
 * TCF2 Policy Configuration Options
 */
export const TCF2_POLICY_OPTIONS = {
  enforceStorage: {
    label: 'Enforce Storage Consent',
    description: 'Require storage consent for bidder storage access',
    default: true
  },
  enforceVendor: {
    label: 'Enforce Vendor Consent',
    description: 'Require specific vendor consent from GVL',
    default: true
  },
  allowAuctionWithoutConsent: {
    label: 'Allow Auctions Without Consent',
    description: 'Allow auctions to run when consent is not available',
    default: false
  },
  activityControls: {
    fetchBids: {
      label: 'Fetch Bids Activity',
      description: 'Control bidder participation in auctions',
      options: ['strict', 'permissive', 'conditional'],
      default: 'permissive'
    },
    accessDevice: {
      label: 'Device Access Activity', 
      description: 'Control bidder device storage access',
      options: ['strict', 'permissive', 'conditional'],
      default: 'conditional'
    },
    transmitTid: {
      label: 'Transmit TID Activity',
      description: 'Control transmission of transaction IDs',
      options: ['strict', 'permissive', 'conditional'],
      default: 'permissive'
    }
  }
};

/**
 * Get default TCF2 policy configuration
 */
export function getDefaultTCF2Policy() {
  return {
    enforceStorage: TCF2_POLICY_OPTIONS.enforceStorage.default,
    enforceVendor: TCF2_POLICY_OPTIONS.enforceVendor.default,
    allowAuctionWithoutConsent: TCF2_POLICY_OPTIONS.allowAuctionWithoutConsent.default,
    activityControls: {
      fetchBids: TCF2_POLICY_OPTIONS.activityControls.fetchBids.default,
      accessDevice: TCF2_POLICY_OPTIONS.activityControls.accessDevice.default,
      transmitTid: TCF2_POLICY_OPTIONS.activityControls.transmitTid.default
    },
    vendorExceptions: []
  };
}

/**
 * Get bidder-specific TCF2 requirements
 */
export function getBidderTCF2Requirements(enabledBidders) {
  const requirements = {
    vendorExceptions: [],
    storageRequired: [],
    purposes: new Set()
  };
  
  enabledBidders.forEach(bidderName => {
    const bidderDef = AVAILABLE_BIDDERS[bidderName];
    if (bidderDef && bidderDef.tcf2) {
      // Collect vendor exceptions
      if (bidderDef.tcf2.needsVendorException) {
        requirements.vendorExceptions.push(bidderName);
      }
      
      // Collect storage requirements
      if (bidderDef.tcf2.requiresStorage) {
        requirements.storageRequired.push(bidderName);
      }
      
      // Collect purposes
      if (bidderDef.tcf2.purposes) {
        bidderDef.tcf2.purposes.forEach(purpose => requirements.purposes.add(purpose));
      }
    }
  });
  
  return {
    ...requirements,
    purposes: Array.from(requirements.purposes)
  };
}